<template>
    <div>
        <search-header
            v-if="!loading"
            :total-results="pagination.totalResults"
            :search-term="search"
            :filters="filters"
            @showModal="() => filtersDialog = true"
            title-label="graphic">
        </search-header>
        <v-row v-if="loading">
            <v-col>
                <v-card
                    class="mt-4"
                    outlined>
                    <v-card-text>
                        <loading-linear>
                        </loading-linear>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col
                v-for="(item, key) in results"
                :key="key"
                sm="3">
                <v-card
                    class="fill-height"
                    @click="getGraphicDetails(item)"
                    outlined>
                    <div
                        @contextmenu="(event) => handleRightClick(event, item.document_sku)"
                        class="img-block-height"
                    >
                        <v-avatar
                            color="white"
                            size="40"
                            style="position: absolute; z-index: 2; top: 2px; right: 2px;">
                            <access-icon :access-level="getAccessLevel(item.document_sku, item.content_access_level_id)">
                            </access-icon>
                        </v-avatar>
                        <div class="text-center">
                            <img
                                :src="item.figure_url"
                                :alt="item.alt_text.length > 0 ? item.alt_text : `${item.section_title} Graphic`"
                                @error="loadPlaceholderImage"
                                height="160px"/>
                        </div>
                    </div>
                    <v-divider>
                    </v-divider>
                    <v-card-text>
                        <v-row
                            v-if="item.section_title"
                            dense>
                            <v-col>
                                <p class="body-2 mb-0 font-weight-bold">
                                    {{item.section_title}}
                                </p>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col>
                                <v-tooltip
                                    v-if="!isMobilePort"
                                    bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-chip
                                            small
                                            color="primaryLight primary--text font-weight-bold"
                                            v-on="on">
                                            <div class="text-truncate">
                                                {{item.document_title}}
                                            </div>
                                        </v-chip>
                                    </template>
                                    <span class="tooltip-content">
                                        {{item.document_title}}
                                    </span>
                                </v-tooltip>
                                <v-chip
                                    v-else
                                    small
                                    color="primaryLight primary--text font-weight-bold">
                                    <div class="text-truncate">
                                        {{item.document_title}}
                                    </div>
                                </v-chip>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row
            v-if="hasMoreResults"
            dense
            justify="center">
            <v-col cols="auto">
                <v-btn
                    text
                    color="primary"
                    class="mb-4"
                    :loading="fetchingResults"
                    @click="showMore">
                    Show More
                    <v-icon right>
                        mdi-chevron-down
                    </v-icon>
                </v-btn>
            </v-col>
        </v-row>
        <v-dialog
            v-model="filtersDialog"
            fullscreen
            scrollable>
            <v-card>
                <v-card-title>
                    <h3>Graphics Filters</h3>
                    <v-spacer></v-spacer>
                    <v-icon
                        @click="filtersDialog = false">
                        close
                    </v-icon>
                </v-card-title>
                <v-card-text class="pa-4">
                    <graphics-search-filters>
                    </graphics-search-filters>
                </v-card-text>
                <v-card-actions>
                    <v-row dense>
                        <v-col>
                            <v-btn
                                block
                                outlined
                                @click="filtersDialog = false"
                                color="primary">
                                Close
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import SearchHeader from "../titles/SearchHeader.vue";
    import {GraphicsApi} from "../../../api/GraphicsApi";
    import AccessIcon from "../../icons/AccessIcon.vue";
    import ConfirmDialog from "../../ConfirmDialog.vue";
    import LoadingLinear from "../../loaders/LoadingLinear.vue";
    import ItemBreadcrumb from "../content/result/item_breadcrumb.vue";
    import {EventNames} from "../../../classes/EventNames";
    import TabbedSearch from "../mixins/TabbedSearch";
    import ItemLink from "../content/result/item_link.vue";
    import GraphicsSearchFilters from "./filters/GraphicsSearchFilters.vue";
    import Mobile from "../../inc/mixins/mobile";
    import GraphicSearchDetails from "./mixins/GraphicSearchDetails";

    export default {
        name: 'GraphicsSearchResults',
        data() {
            return {
                search: '',
                page: 1,
                limit: 12,
                results: [],
                pagination: {
                    currentPage: 1,
                    totalResults: 0,
                    maxPerPage: 0
                },
                categories: [],
                filters: {
                    categories: [],
                    years: []
                },
                loading: true,
                fetchingResults: true,
                dialogLoading: false,
                dialog: false,
                filtersDialog: false,
                premiumRequiredDialog: false,
                graphicDetails: {},
                resetAllData: false,
                searchTermId: null,
                accessLevelMsg: {
                    basic: 'Basic',
                    premium: 'Premium Required',
                    premiumActive: 'Premium Active'
                },
                defaultGraphicTabCategory:  '2024 I-Codes'
            }
        },
        mixins: [
            TabbedSearch,
            Mobile,
            GraphicSearchDetails
        ],
        components: {
            GraphicsSearchFilters,
            ItemLink,
            ItemBreadcrumb,
            LoadingLinear,
            ConfirmDialog,
            AccessIcon,
            SearchHeader
        },
        computed: {
            hasMoreResults() {
                return this.results.length > 0 && this.results.length < this.pagination.totalResults;
            }
        },
        watch: {
            search(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.$store.commit('setTabbedSearchValues', {
                        tab: this.$getConst('graphicsSearchType'),
                        searchValue: newVal
                    });
                }
            },
            filterCategories: {
                handler() {
                    if (this.resetAllData) {
                        return;
                    }
                    this.refreshData();
                },
                deep: true
            },
            filterYears: {
                handler() {
                    if (this.resetAllData) {
                        return;
                    }
                    this.refreshData();
                },
                deep: true
            },
            filterTitleIds: {
                handler() {
                    if (this.resetAllData) {
                        return;
                    }
                    this.refreshData();
                },
                deep: true
            },
        },
        computed: {
            hasMoreResults() {
                return this.results.length > 0 && this.results.length < this.pagination.totalResults;
            }
        },
        methods: {
            showMore(){
                this.page = this.page + 1;
                this.getResults();
            },
            async getResults() {
                if (!this.isGraphicsTab()) {
                    return;
                }
                this.fetchingResults = true;

                const categories = this.filterCategories[this.$getConst('graphicsSearchType')] || [];
                const years = this.filterYears[this.$getConst('graphicsSearchType')] || [];
                const titles = this.filterTitleIds[this.$getConst('graphicsSearchType')] || [];
                const data = {
                    search: this.search,
                    titles: titles,
                    page: this.page,
                    limit: this.limit,
                    years: years,
                    categories: categories
                }

                const url = new URL(window.location);
                const params = new URLSearchParams(url.search);
                if (params.get('ai_search') === 'off') {
                    data.ai_search = params.get('ai_search');
                }
                if (params.get('ai_weight')) {
                    data.ai_weight = params.get('ai_weight');
                }
                if (params.get('content_search') === 'off') {
                    data.content_search = params.get('content_search');
                }
                if (params.get('deduping') === 'off') {
                    data.deduping = params.get('deduping');
                }
                if (params.get('ai_search_k')) {
                  data.ai_search_k = params.get('ai_search_k');
                }
                if (params.get('ai_search_num_candidates')) {
                  data.ai_search_num_candidates = params.get('ai_search_num_candidates');
                }

                // Update search term in the URL
                if (this.search) {
                    params.set('searchTermAny', this.search);
                } else {
                    params.delete('searchTermAny');
                }

                params.delete('categories');
                params.delete('years');
                params.delete('titles');

                categories.forEach(v => params.append('categories', v));
                years.forEach(v => params.append('years', v));
                titles.forEach(v => params.append('titles', v));

                // Update the URL without reloading the page
                window.history.pushState({}, "", `?${params.toString()}`);

                this.$store.commit('setTabbedSearchUrl', {
                    tab: this.$getConst('graphicsSearchType'),
                    searchUrl: `?${params.toString()}`,
                    updateSearchTerm: true
                });

                try {
                    const response = await GraphicsApi.getGraphicsResults(data);
                    const newData = response.data.graphics;
                    this.results.push(...newData);
                    this.pagination = response.data.pagination;
                    this.loading = false;
                    this.fetchingResults = false;
                    this.searchTermId = response.data.searchTermId;
                } catch (error) {
                    // Todo: Handle error
                    console.log(error)
                }
                this.resetAllData = false;
            },
            resetData(){
                this.resetAllData = true;
                this.page = 1;
                this.results = [];
                this.categories = [];
                this.years = [];
                this.loading = true;

                EventBus.fire(EventNames.REMOVE_ALL_ACTIVE_CATEGORIES);
                EventBus.fire(EventNames.REMOVE_ALL_ACTIVE_YEARS);
                EventBus.fire(EventNames.REMOVE_ALL_ACTIVE_TITLES);

                this.getResults();
            },
            refreshData(){
                if (!this.isGraphicsTab()) {
                    return;
                }
                this.page = 1;
                this.results = [];
                this.loading = true;
                this.getResults();
            },
            getAccessLevel(sku, contentAccessLevelId) {
                if (this.isPremiumSubscribed(sku)) {
                    return 'premiumActive';
                }

                if (this.isBasicAccessBook(contentAccessLevelId)) {
                    return 'basic';
                }

                return 'premium';
            },
            loadPlaceholderImage(e) {
                e.target.src = this.asset(require('../../../images/graphics_image_missing.png'));
            },
            handleRightClick(event, sku) {
                if (!this.isPremiumSubscribed(sku)) {
                    event.preventDefault();
                }
            },
            isGraphicsTab() {
                return this.searchTab === this.$getConst('graphicsSearchType');
            },
            isGraphicsSearchUrl() {
                return window.location.href.includes('search/graphics');
            },
            getGraphicDetails(item) {
                item.searchTermId = this.searchTermId;
                EventBus.fire(EventNames.GRAPHIC_DETAILS, item);
            }
        },
        created() {
            EventBus.listen(EventNames.INIT_GRAPHICS_SEARCH, () => {
                this.resetAllData = true;
                const url = new URL(window.location.href);
                const params = new URLSearchParams(url.search);

                this.search  = params.get('searchTermAny') || '';
                this.defaultSearch = this.search;


                let categories = [];
                let years = [];
                let titles = [];
                let filterApplied = false;
                if(this.isGraphicsSearchUrl()) {
                    categories = params.getAll('categories');
                    years = params.getAll('years');
                    titles = params.getAll('titles');
                }

                if (categories && categories.length > 0) {
                    this.$store.commit('setTabbedCategories', {
                        tab: this.$getConst('graphicsSearchType'),
                        categories: categories
                    });
                    filterApplied = true;
                }
                if (years && years.length > 0) {
                    this.$store.commit('setTabbedYears', {
                        tab: this.$getConst('graphicsSearchType'),
                        years: years
                    });
                    filterApplied = true;
                }
                if (titles && titles.length > 0) {
                    titles = titles.map(title => parseInt(title, 10));
                    this.$store.commit('setTabbedTitleIds', {
                        tab: this.$getConst('graphicsSearchType'),
                        titles: titles
                    });
                    filterApplied = true;
                }
                if (!filterApplied && !this.search){
                    this.$store.commit('setTabbedCategories', {
                        tab: this.$getConst('graphicsSearchType'),
                        categories: [this.defaultGraphicTabCategory]
                    });
                }

                this.getResults();
            });
            EventBus.listen(EventNames.UPDATE_GRAPHICS_SEARCH, (data) => {
                if (this.search !== data) {
                    this.search = data;
                    this.refreshData();
                }
            });
            EventBus.listen(EventNames.RESET_GRAPHICS_SEARCH, () => {
                this.resetData();
            });
        },
        mounted() {
            document.addEventListener("keydown", (e) => {
                if ('Escape' === e.key) {
                    e.preventDefault();
                    this.dialog = false;
                    this.premiumRequiredDialog = false;
                    this.filtersDialog = false;
                }
            });
        }
    }
</script>

<style scoped>
    .img-block-height {
        height: 160px;
        overflow: hidden;
    }
</style>